/**
 * Instance create owner_entity_id picker.
 *
 * Distinct from Conta root pickers in baasRootAccountOptions.js:
 * - Exit-rail / matriz Conta root: PERSON/PF only (incl. pending).
 * - Partner owner RootAccountId: PF + PJ accounts.
 * - This module: BUSINESS / enti_ profiles for owner_entity_id (API requires BUSINESS).
 */

import { fetchBaasEntities } from "../../../../Services/baasEntidadesApi";

const normalizeType = (entity) => String(entity?.type || "").trim().toLowerCase();

const normalizeId = (entity) => String(entity?.id || "").trim();

const BLOCKED_OWNER_KYB = new Set(["rejected"]);
const BLOCKED_OWNER_VERIFICATION = new Set(["REJECTED", "SUSPENDED", "BLOCKED"]);
const BLOCKED_OWNER_STATUS = new Set(["suspended", "blocked", "rejected"]);

/**
 * Owner KYB for instance create: pending / in-analysis / approved are OK.
 * Rejected, suspended, or blocked entities cannot own a new program.
 * Production stays unliberated until Valut Aprovar produção.
 */
export const isBaasOwnerEntityKybAllowed = (entity) => {
  const kyb = String(entity?.kybStatus || "").trim().toLowerCase();
  const verification = String(entity?.verificationStatus || "").trim().toUpperCase();
  const status = String(entity?.status || "").trim().toLowerCase();

  if (BLOCKED_OWNER_KYB.has(kyb)) return false;
  if (BLOCKED_OWNER_VERIFICATION.has(verification)) return false;
  if (BLOCKED_OWNER_STATUS.has(status)) return false;

  return true;
};

/** @deprecated Use isBaasOwnerEntityKybAllowed — create no longer requires KYB approved. */
export const isBaasOwnerEntityActive = isBaasOwnerEntityKybAllowed;

/**
 * Eligible instance owners: BUSINESS / PJ / enti_ only (not pers_ / PF).
 * Pending / in-analysis KYB is allowed; drops rejected/suspended and person ids.
 */
export const isEligibleBaasOwnerEntity = (entity) => {
  const id = normalizeId(entity);
  if (!id) return false;
  if (/^pers_/i.test(id)) return false;

  const type = normalizeType(entity);
  const isPj =
    type === "pj"
    || type === "business"
    || /^enti_/i.test(id);
  if (!isPj) return false;

  return isBaasOwnerEntityKybAllowed(entity);
};

export const formatBaasOwnerEntityOptionLabel = (entity) => {
  const name = String(entity?.name || "").trim() || normalizeId(entity);
  const document = String(entity?.document || "").trim();
  return document ? `${name} (${document})` : name;
};

export const buildBaasOwnerEntitySelectOptions = (entities = []) => {
  const byId = new Map();
  for (const entity of Array.isArray(entities) ? entities : []) {
    if (!isEligibleBaasOwnerEntity(entity)) continue;
    const id = normalizeId(entity);
    if (byId.has(id)) continue;
    byId.set(id, {
      id,
      label: formatBaasOwnerEntityOptionLabel(entity),
      status: String(entity?.status || "").trim().toLowerCase() || null,
      kybStatus: String(entity?.kybStatus || "").trim().toLowerCase() || null,
    });
  }

  return [...byId.values()].sort((left, right) => left.label.localeCompare(right.label));
};

/**
 * Loads BUSINESS entities for owner_entity_id.
 * Prefers unscoped=true so Valut AllowGlobalAccess keys are not limited to the
 * create-parent card’s linked rows (matrix pin otherwise drops most PJ actives).
 * Falls back to the pinned instance list when unscoped is forbidden.
 * Exit-rail Conta root stays PERSON-only elsewhere — do not reuse this loader there.
 */
export const loadBaasOwnerEntitySelectOptions = async (instance) => {
  if (!instance) return [];

  let entities = await fetchBaasEntities(instance, {
    entityType: "BUSINESS",
    unscoped: true,
  }).catch(() => null);

  if (!Array.isArray(entities)) {
    entities = await fetchBaasEntities(instance, {
      entityType: "BUSINESS",
    }).catch(() => []);
  }

  return buildBaasOwnerEntitySelectOptions(entities);
};
